"use client"

import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import API from "../services/api"

function LoginPage() {
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [focused, setFocused] = useState({ email: false, password: false })
  const navigate = useNavigate()

  useEffect(() => {
    // Add CSS animations
    if (!document.getElementById('login-animations')) {
      const style = document.createElement('style')
      style.id = 'login-animations'
      style.textContent = `
        @keyframes float {
          0%, 100% {
            transform: translate(0, 0) rotate(0deg);
          }
          33% {
            transform: translate(30px, -30px) rotate(120deg);
          }
          66% {
            transform: translate(-20px, 20px) rotate(240deg);
          }
        }
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        [data-login-form] {
          animation: fadeIn 0.5s ease-in-out;
        }
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        [data-login-circle] {
          animation: float 8s ease-in-out infinite;
        }
        [data-login-circle]:nth-of-type(1) {
          animation-duration: 6s;
        }
        [data-login-circle]:nth-of-type(2) {
          animation-duration: 8s;
          animation-delay: -2s;
        }
        [data-login-circle]:nth-of-type(3) {
          animation-duration: 10s;
          animation-delay: -4s;
        }
        [data-login-spinner] {
          animation: spin 1s linear infinite;
        }
        button[data-login-button]:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(133, 104, 240, 0.5) !important;
        }
        button[data-login-button]:active:not(:disabled) {
          transform: translateY(0);
        }
        button[data-login-home]:hover:not(:disabled) {
          background: #f5f5f5 !important;
          border-color: #8568f0 !important;
          color: #8568f0 !important;
          transform: translateY(-1px);
        }
        button[data-login-password-toggle]:hover {
          transform: scale(1.1);
        }
        @keyframes shake {
          0%, 100% {
            transform: translateX(0);
          }
          10%, 30%, 50%, 70%, 90% {
            transform: translateX(-4px);
          }
          20%, 40%, 60%, 80% {
            transform: translateX(4px);
          }
        }
        @media (max-width: 480px) {
          [data-login-form] {
            padding: 40px 28px !important;
            margin: 10px;
          }
        }
      `
      document.head.appendChild(style)
    }
  }, [])

  const handleLogin = async (e) => {
    e.preventDefault()
    setError("")
    setLoading(true)
    
    try {
      const response = await API.post("/auth/login", { email, password })
      localStorage.setItem("token", response.data.token)
      localStorage.setItem("user", JSON.stringify(response.data.user))
      navigate("/dashboard")
    } catch (err) {
      setError(err.response?.data?.message || "Login failed. Please check your credentials.")
    } finally {
      setLoading(false)
    }
  }

  const handleFocus = (field) => {
    setFocused(prev => ({ ...prev, [field]: true }))
  }

  const handleBlur = (field) => {
    setFocused(prev => ({ ...prev, [field]: false }))
  }

  return (
    <div style={styles.container}>
      <div style={styles.backgroundDecoration}>
        <div style={styles.circle1} data-login-circle></div>
        <div style={styles.circle2} data-login-circle></div>
        <div style={styles.circle3} data-login-circle></div>
      </div>
      
      <form onSubmit={handleLogin} style={styles.form} data-login-form>
        <button 
          type="button" 
          onClick={() => navigate("/home")} 
          style={styles.homeButton}
          disabled={loading}
          data-login-home
          title="Accueil / Home"
        >
          <span style={styles.homeIcon}>⌂</span>
        </button>

        <div style={styles.logoContainer}>
          <img src="/GUESTLY_DARK.jpg" alt="Logo" style={styles.logo} />
        </div>
        
        <h2 style={styles.title}>Welcome Back</h2>
        <p style={styles.subtitle}>Sign in to continue to your dashboard</p>

        {error && (
          <div style={styles.errorContainer}>
            <span style={styles.errorIcon}>!</span>
            <p style={styles.error}>{error}</p>
          </div>
        )}

        <div style={styles.inputContainer}>
          <label style={styles.label}>Email</label>
          <div style={{
            ...styles.inputWrapper,
            ...(focused.email ? styles.inputWrapperFocused : {}),
            ...(error && !email ? styles.inputWrapperError : {})
          }}>
            <span style={styles.inputIcon}>@</span>
            <input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
                setError("")
              }}
              onFocus={() => handleFocus("email")}
              onBlur={() => handleBlur("email")}
              style={styles.input}
              required
              disabled={loading}
            />
          </div>
        </div>

        <div style={styles.inputContainer}>
          <label style={styles.label}>Password</label>
          <div style={{
            ...styles.inputWrapper,
            ...(focused.password ? styles.inputWrapperFocused : {}),
            ...(error && !password ? styles.inputWrapperError : {})
          }}>
            <span style={styles.inputIcon}>*</span>
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Enter your password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value)
                setError("")
              }}
              onFocus={() => handleFocus("password")}
              onBlur={() => handleBlur("password")}
              style={styles.input}
              required
              disabled={loading}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              style={styles.passwordToggle}
              tabIndex={-1}
              disabled={loading}
              data-login-password-toggle
              title={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? "Hide" : "Show"}
            </button>
          </div>
        </div>

        <button 
          type="submit" 
          style={{
            ...styles.button,
            ...(loading ? styles.buttonLoading : {}),
            ...(loading ? styles.buttonDisabled : {})
          }}
          disabled={loading}
          data-login-button
        >
          {loading ? (
            <>
              <span style={styles.spinner} data-login-spinner></span>
              Connecting...
            </>
          ) : (
            "Login"
          )}
        </button>

      </form>
    </div>
  )
}

const styles = {
  container: {
    minHeight: "100vh",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    background: "linear-gradient(135deg, #e6d0f7, #cce1fa)",
    position: "relative",
    overflow: "hidden",
    padding: "20px",
  },
  backgroundDecoration: {
    position: "absolute",
    width: "100%",
    height: "100%",
    top: 0,
    left: 0,
    zIndex: 0,
  },
  circle1: {
    position: "absolute",
    width: "300px",
    height: "300px",
    borderRadius: "50%",
    background: "rgba(255, 255, 255, 0.1)",
    top: "-100px",
    left: "-100px",
  },
  circle2: {
    position: "absolute",
    width: "200px",
    height: "200px",
    borderRadius: "50%",
    background: "rgba(255, 255, 255, 0.08)",
    bottom: "-50px",
    right: "-50px",
  },
  circle3: {
    position: "absolute",
    width: "150px",
    height: "150px",
    borderRadius: "50%",
    background: "rgba(255, 255, 255, 0.06)",
    top: "50%",
    right: "10%",
  },
  form: {
    background: "#fff",
    padding: "50px 45px",
    borderRadius: "24px",
    boxShadow: "0 12px 40px rgba(0, 0, 0, 0.15)",
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    width: "100%",
    maxWidth: "500px",
    position: "relative",
    zIndex: 1,
    transition: "transform 0.3s ease, box-shadow 0.3s ease",
  },
  logoContainer: {
    display: "flex",
    justifyContent: "center",
    marginBottom: "20px",
  },
  logo: {
    width: "200px",
    height: "auto",
    marginBottom: "30px",
    transition: "transform 0.3s ease",
  },
  title: {
    marginBottom: "32px",
    color: "#222",
    fontWeight: 700,
    fontSize: "26px",
    textAlign: "center",
  },
  subtitle: {
    marginBottom: "20px",
    color: "#666",
    fontSize: "14px",
    textAlign: "center",
    fontWeight: 400,
  },
  inputContainer: {
    width: "100%",
    marginBottom: "24px",
  },
  label: {
    display: "block",
    marginBottom: "8px",
    color: "#333",
    fontSize: "14px",
    fontWeight: 600,
  },
  inputWrapper: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    borderRadius: "12px",
    border: "2px solid #e0e0e0",
    background: "#fafafa",
    transition: "all 0.3s ease",
    overflow: "hidden",
  },
  inputWrapperFocused: {
    borderColor: "#8568f0",
    background: "#fff",
    boxShadow: "0 0 0 4px rgba(133, 104, 240, 0.1)",
    transform: "translateY(-1px)",
  },
  inputWrapperError: {
    borderColor: "#ef4444",
    background: "#fff5f5",
  },
  inputIcon: {
    padding: "0 12px 0 16px",
    fontSize: "16px",
    fontWeight: 600,
    color: "#8568f0",
    userSelect: "none",
  },
  input: {
    flex: 1,
    padding: "16px 12px 16px 0",
    fontSize: "15px",
    border: "none",
    outline: "none",
    background: "transparent",
    color: "#1a1a1a",
    fontWeight: 400,
  },
  passwordToggle: {
    background: "none",
    border: "none",
    padding: "0 16px",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: 600,
    color: "#8568f0",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "transform 0.2s ease",
    textTransform: "uppercase",
  },
  button: {
    padding: "14px 18px",
    fontSize: "16px",
    fontWeight: "bold",
    borderRadius: "10px",
    background: "#8568f0",
    color: "#fff",
    border: "none",
    cursor: "pointer",
    width: "100%",
    transition: "0.3s",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    marginTop: "8px",
  },
  buttonLoading: {
    cursor: "not-allowed",
    opacity: 0.8,
  },
  buttonDisabled: {
    pointerEvents: "none",
  },
  spinner: {
    display: "inline-block",
    width: "16px",
    height: "16px",
    border: "2px solid rgba(255, 255, 255, 0.3)",
    borderTopColor: "#fff",
    borderRadius: "50%",
  },
  homeButton: {
    position: "absolute",
    top: "20px",
    right: "20px",
    padding: "12px",
    fontSize: "14px",
    fontWeight: 600,
    borderRadius: "50%",
    background: "#f5f5f5",
    color: "#666",
    border: "2px solid #e0e0e0",
    cursor: "pointer",
    width: "50px",
    height: "50px",
    transition: "all 0.3s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
    zIndex: 10,
  },
  homeIcon: {
    fontSize: "24px",
    lineHeight: 1,
    fontWeight: 300,
  },
  errorContainer: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "14px 18px",
    marginBottom: "24px",
    background: "#fee2e2",
    border: "1px solid #fecaca",
    borderRadius: "10px",
    color: "#991b1b",
  },
  errorIcon: {
    fontSize: "20px",
    fontWeight: "bold",
    flexShrink: 0,
    width: "24px",
    height: "24px",
    borderRadius: "50%",
    background: "#991b1b",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: 1,
  },
  error: {
    color: "#991b1b",
    margin: 0,
    fontSize: "14px",
    fontWeight: 500,
    flex: 1,
  },
  signupLink: {
    marginTop: "20px",
    fontSize: "14px",
    color: "#666",
    textAlign: "center",
  },
  link: {
    color: "#8568f0",
    textDecoration: "none",
    fontWeight: 600,
    transition: "color 0.2s ease",
  },
}

export default LoginPage
